import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Sunrise, Sunset } from 'lucide-react';
import { useI18n } from '../context/I18nContext';

const Timeline2D = ({ latitude, longitude, sunTimes, onTimeChange }) => {
  const { t } = useI18n();
  const [selectedHour, setSelectedHour] = useState(new Date().getHours());
  const [isDragging, setIsDragging] = useState(false);

  // Calculate sun position for each hour
  const sunPositions = useMemo(() => {
    const positions = [];
    for (let h = 0; h < 24; h++) {
      const dt = new Date();
      dt.setHours(h, 0, 0, 0);
      
      const dayOfYear = Math.floor((dt - new Date(dt.getFullYear(), 0, 0)) / 86400000);
      const declination = 23.45 * Math.sin((360 / 365) * (dayOfYear - 81) * Math.PI / 180);
      
      const solarNoon = 12.0 - longitude / 15.0;
      const hourAngle = 15 * (h - solarNoon);
      
      const latRad = latitude * Math.PI / 180;
      const decRad = declination * Math.PI / 180;
      const haRad = hourAngle * Math.PI / 180;
      
      const sinAlt = Math.sin(latRad) * Math.sin(decRad) + 
                     Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);
      const altitude = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * 180 / Math.PI;
      
      positions.push({
        hour: h,
        altitude: altitude,
        isDay: altitude > 0,
        isGoldenHour: altitude > 0 && altitude <= 6,
        isBlueHour: altitude >= -6 && altitude < 0,
        isNight: altitude < -6
      });
    }
    return positions;
  }, [latitude, longitude]);

  // Parse time string to hour
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const [hours] = timeStr.split(':').map(Number);
    return hours;
  };

  const sunriseHour = parseTime(sunTimes?.sunrise);
  const sunsetHour = parseTime(sunTimes?.sunset);
  const currentHour = new Date().getHours();

  // Get color for position
  const getPositionColor = (pos) => {
    if (pos.isGoldenHour) return '#F59E0B';
    if (pos.isBlueHour) return '#3B82F6';
    if (pos.isDay) return '#FBBF24';
    return '#1E293B';
  };

  // Handle timeline click/drag
  const handleTimelineInteraction = (e, element) => {
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const hour = Math.floor((x / rect.width) * 24);
    const clampedHour = Math.max(0, Math.min(23, hour));
    setSelectedHour(clampedHour);
    if (onTimeChange) {
      const newDate = new Date();
      newDate.setHours(clampedHour, 0, 0, 0);
      onTimeChange(newDate);
    }
  };

  return (
    <div className="w-full bg-slate-900/50 rounded-xl border border-white/10 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg text-white flex items-center gap-2">
          <Sun className="w-5 h-5 text-amber-500" />
          {t('timeline.title')}
        </h3>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span className="text-slate-400">{t('sun.goldenHour')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="text-slate-400">{t('sun.blueHour')}</span>
          </div>
        </div>
      </div>

      {/* Sun Arc Visualization */}
      <div className="relative h-32 mb-4">
        <svg viewBox="0 0 480 120" className="w-full h-full">
          {/* Background gradient */}
          <defs>
            <linearGradient id="skyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0F172A" />
              <stop offset="50%" stopColor="#1E293B" />
              <stop offset="100%" stopColor="#334155" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="480" height="120" fill="url(#skyGradient)" rx="8" />
          
          {/* Horizon line */}
          <line x1="0" y1="80" x2="480" y2="80" stroke="#475569" strokeWidth="1" strokeDasharray="4,4" />
          <text x="10" y="78" fill="#64748B" fontSize="10">{t('timeline.horizon')}</text>

          {/* Sun path */}
          <path
            d={sunPositions.length > 0 ? sunPositions.map((pos, i) => {
              const x = (i / 23) * 460 + 10;
              const y = 80 - (pos.altitude / 90) * 70;
              const safeY = isNaN(y) ? 80 : Math.max(10, Math.min(110, y));
              return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${safeY.toFixed(1)}`;
            }).join(' ') : 'M 0 80'}
            fill="none"
            stroke="#F59E0B"
            strokeWidth="2"
            opacity="0.5"
          />

          {/* Golden hour zones */}
          {sunPositions.map((pos, i) => {
            if (!pos.isGoldenHour && !pos.isBlueHour) return null;
            const x = (i / 23) * 460 + 10;
            return (
              <rect
                key={`zone-${i}`}
                x={x - 10}
                y="0"
                width="20"
                height="120"
                fill={pos.isGoldenHour ? '#F59E0B' : '#3B82F6'}
                opacity="0.1"
              />
            );
          })}

          {/* Sun position markers */}
          {sunPositions.filter((_, i) => i % 3 === 0).map((pos, idx) => {
            const i = idx * 3;
            const x = (i / 23) * 460 + 10;
            const y = 80 - (pos.altitude / 90) * 70;
            return (
              <g key={`sun-${i}`}>
                <circle
                  cx={x}
                  cy={Math.max(10, Math.min(110, y))}
                  r={i === selectedHour ? 8 : 4}
                  fill={getPositionColor(pos)}
                  stroke={i === currentHour ? '#fff' : 'none'}
                  strokeWidth="2"
                />
              </g>
            );
          })}

          {/* Selected hour sun */}
          {(() => {
            const pos = sunPositions[selectedHour];
            const x = (selectedHour / 23) * 460 + 10;
            const y = 80 - (pos.altitude / 90) * 70;
            return (
              <g>
                <circle
                  cx={x}
                  cy={Math.max(10, Math.min(110, y))}
                  r="10"
                  fill={getPositionColor(pos)}
                  stroke="#fff"
                  strokeWidth="2"
                  className="drop-shadow-lg"
                />
                <circle
                  cx={x}
                  cy={Math.max(10, Math.min(110, y))}
                  r="16"
                  fill="none"
                  stroke={getPositionColor(pos)}
                  strokeWidth="1"
                  opacity="0.5"
                />
              </g>
            );
          })()}

          {/* Sunrise/Sunset markers */}
          {sunriseHour !== null && (
            <g>
              <line
                x1={(sunriseHour / 23) * 460 + 10}
                y1="0"
                x2={(sunriseHour / 23) * 460 + 10}
                y2="120"
                stroke="#F59E0B"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
              <Sunrise
                x={(sunriseHour / 23) * 460}
                y="85"
                className="w-5 h-5 text-amber-500"
              />
            </g>
          )}
          {sunsetHour !== null && (
            <g>
              <line
                x1={(sunsetHour / 23) * 460 + 10}
                y1="0"
                x2={(sunsetHour / 23) * 460 + 10}
                y2="120"
                stroke="#F97316"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
            </g>
          )}
        </svg>
      </div>

      {/* Interactive Timeline Bar */}
      <div 
        className="relative h-12 rounded-lg overflow-hidden cursor-pointer select-none"
        onMouseDown={(e) => {
          setIsDragging(true);
          handleTimelineInteraction(e, e.currentTarget);
        }}
        onMouseMove={(e) => {
          if (isDragging) {
            handleTimelineInteraction(e, e.currentTarget);
          }
        }}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
        data-testid="timeline-bar"
      >
        {/* Hour segments */}
        <div className="absolute inset-0 flex">
          {sunPositions.map((pos, i) => (
            <div
              key={i}
              className="flex-1 transition-all duration-150"
              style={{ backgroundColor: getPositionColor(pos) }}
            />
          ))}
        </div>

        {/* Hour labels */}
        <div className="absolute inset-0 flex items-end pb-1">
          {[0, 6, 12, 18, 23].map((h) => (
            <div
              key={h}
              className="absolute text-xs font-mono text-white/70"
              style={{ left: `${(h / 23) * 100}%`, transform: 'translateX(-50%)' }}
            >
              {h.toString().padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {/* Current time indicator */}
        <motion.div
          className="absolute top-0 bottom-0 w-0.5 bg-white"
          style={{ left: `${(currentHour / 23) * 100}%` }}
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />

        {/* Selected time indicator */}
        <div
          className="absolute top-0 h-full w-1 bg-white rounded-full shadow-lg"
          style={{ left: `${(selectedHour / 23) * 100}%`, transform: 'translateX(-50%)' }}
        />
      </div>

      {/* Selected Time Info */}
      <div className="mt-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500">{t('timeline.selectedTime')}</p>
          <p className="font-mono text-2xl text-white">{selectedHour.toString().padStart(2, '0')}:00</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">{t('timeline.sunAltitude')}</p>
          <p className="font-mono text-2xl text-white">
            {sunPositions[selectedHour]?.altitude.toFixed(1)}°
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">{t('timeline.conditions')}</p>
          <p className="text-lg font-medium" style={{ color: getPositionColor(sunPositions[selectedHour]) }}>
            {sunPositions[selectedHour]?.isGoldenHour ? t('sun.goldenHour') :
             sunPositions[selectedHour]?.isBlueHour ? t('sun.blueHour') :
             sunPositions[selectedHour]?.isDay ? t('ephemeris.daylight') : t('timeline.night')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Timeline2D;
